/**
 * tokens.js — Design tokens FG-OS
 *
 * Source de verite visuelle. Miroir exact de DESIGN.md.
 * Tous les tokens couleur sont exportes en JS et injectes comme
 * CSS variables sur :root[data-theme="light"] et :root[data-theme="dark"].
 *
 * Bascule jour/nuit : applyTheme('light' | 'dark').
 * Usage dans App.jsx : style={{ background: 'var(--bg)', color: 'var(--text)' }}.
 *
 * Convention nommage JS -> CSS var :
 *   bgElevated -> --bg-elevated
 *   accentHover -> --accent-hover
 *   borderFocus -> --border-focus
 */

// ============================================================
// Tokens couleur (OKLCH, neutres tintes vert tech hue 145)
// ============================================================
export const tokens = {
  light: {
    // Surfaces (neutres tintes vert, chroma faible 0.002-0.006)
    bg:             'oklch(99.0% 0.004 145)',
    bgElevated:     'oklch(99.8% 0.002 145)',
    bgSubtle:       'oklch(97.0% 0.005 145)',
    bgHover:        'oklch(95.5% 0.006 145)',

    // Bordures
    border:         'oklch(92.0% 0.006 145)',
    borderStrong:   'oklch(85.0% 0.008 145)',
    borderFocus:    'oklch(64.0% 0.180 145)',

    // Texte
    text:           'oklch(20.0% 0.010 145)',
    textMuted:      'oklch(45.0% 0.010 145)',
    textSubtle:     'oklch(60.0% 0.008 145)',
    textOnAccent:   'oklch(99.0% 0.004 145)',

    // Accent vert tech (G&G Greener Future), signal, <= 10% surface
    accent:         'oklch(64.0% 0.180 145)',
    accentHover:    'oklch(54.0% 0.160 145)',
    accentSubtle:   'oklch(94.0% 0.050 145)',
    accentStrong:   'oklch(48.0% 0.150 145)',

    // Semantique
    danger:         'oklch(58.0% 0.220  25)',
    dangerSubtle:   'oklch(94.0% 0.040  25)',
    warning:        'oklch(70.0% 0.160  70)',
    warningSubtle:  'oklch(95.0% 0.050  70)',
    info:           'oklch(60.0% 0.140 240)',
    infoSubtle:     'oklch(94.0% 0.040 240)',

    // Ombres (RGB pour compat box-shadow)
    shadowSm:       '0 1px 2px rgb(0 0 0 / 0.04)',
    shadowMd:       '0 4px 12px rgb(0 0 0 / 0.06)',
    shadowLg:       '0 16px 40px rgb(0 0 0 / 0.10)',
    shadowModal:    '0 24px 60px rgb(0 0 0 / 0.18)',

    // Overlay modale
    overlay:        'rgb(0 0 0 / 0.40)',
  },

  dark: {
    bg:             'oklch(15.0% 0.005 145)',
    bgElevated:     'oklch(20.0% 0.006 145)',
    bgSubtle:       'oklch(25.0% 0.007 145)',
    bgHover:        'oklch(28.0% 0.008 145)',

    border:         'oklch(28.0% 0.008 145)',
    borderStrong:   'oklch(38.0% 0.010 145)',
    borderFocus:    'oklch(70.0% 0.200 145)',

    text:           'oklch(96.0% 0.005 145)',
    textMuted:      'oklch(72.0% 0.008 145)',
    textSubtle:     'oklch(55.0% 0.008 145)',
    textOnAccent:   'oklch(15.0% 0.005 145)',

    // Saturation accent boostee en dark (HIG/MD)
    accent:         'oklch(70.0% 0.200 145)',
    accentHover:    'oklch(60.0% 0.180 145)',
    accentSubtle:   'oklch(22.0% 0.060 145)',
    accentStrong:   'oklch(78.0% 0.180 145)',

    danger:         'oklch(65.0% 0.220  25)',
    dangerSubtle:   'oklch(22.0% 0.060  25)',
    warning:        'oklch(75.0% 0.160  70)',
    warningSubtle:  'oklch(24.0% 0.060  70)',
    info:           'oklch(68.0% 0.140 240)',
    infoSubtle:     'oklch(22.0% 0.060 240)',

    shadowSm:       '0 1px 2px rgb(0 0 0 / 0.40)',
    shadowMd:       '0 4px 12px rgb(0 0 0 / 0.50)',
    shadowLg:       '0 16px 40px rgb(0 0 0 / 0.60)',
    shadowModal:    '0 24px 60px rgb(0 0 0 / 0.80)',

    overlay:        'rgb(0 0 0 / 0.60)',
  },
};

// ============================================================
// Echelle d'espacement (base 4px)
// ============================================================
export const space = {
  xs:    '0.25rem', //  4px
  sm:    '0.5rem',  //  8px
  md:    '0.75rem', // 12px
  lg:    '1rem',    // 16px
  xl:    '1.5rem',  // 24px
  '2xl': '2rem',    // 32px
  '3xl': '3rem',    // 48px
  '4xl': '4rem',    // 64px
};

// ============================================================
// Rayons
// ============================================================
export const radius = {
  sm:    '6px',    // chips, inputs, boutons compacts
  md:    '10px',   // cartes, boutons standard
  lg:    '16px',   // modales, panneaux
  full:  '9999px', // badges pill, avatars
};

// ============================================================
// Typographie
// ============================================================
export const typo = {
  family: {
    display: "'Space Grotesk', -apple-system, BlinkMacSystemFont, sans-serif",
    body:    "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    mono:    "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
  },

  scale: {
    'display-xl': { size: '2rem',     lineHeight: 1.10, weight: 700, family: 'display' }, // 32px
    'display-lg': { size: '1.5rem',   lineHeight: 1.20, weight: 600, family: 'display' }, // 24px
    'display-md': { size: '1.125rem', lineHeight: 1.30, weight: 600, family: 'display' }, // 18px
    'body-lg':    { size: '1rem',     lineHeight: 1.55, weight: 400, family: 'body' },    // 16px
    'body-md':    { size: '0.875rem', lineHeight: 1.50, weight: 400, family: 'body' },    // 14px
    'body-sm':    { size: '0.75rem',  lineHeight: 1.45, weight: 500, family: 'body' },    // 12px
    'mono-md':    { size: '0.875rem', lineHeight: 1.40, weight: 500, family: 'mono' },    // 14px
    'mono-lg':    { size: '1.25rem',  lineHeight: 1.20, weight: 500, family: 'mono' },    // 20px
    'mono-xl':    { size: '2rem',     lineHeight: 1.10, weight: 600, family: 'mono' },    // 32px hero CA
  },
};

/**
 * Retourne un objet style React pour une echelle typo donnee.
 * Les chiffres mono recoivent automatiquement font-variant-numeric: tabular-nums
 * (empeche les sauts de layout sur les compteurs et tableaux de prix).
 *
 * Exemple : <h1 style={{ ...typoStyle('display-lg'), color: 'var(--text)' }}>...</h1>
 */
export function typoStyle(scaleName) {
  const s = typo.scale[scaleName];
  if (!s) {
    console.warn(`[tokens] typoStyle: echelle "${scaleName}" inconnue`);
    return {};
  }
  return {
    fontSize: s.size,
    lineHeight: s.lineHeight,
    fontWeight: s.weight,
    fontFamily: typo.family[s.family],
    fontVariantNumeric: s.family === 'mono' ? 'tabular-nums' : undefined,
  };
}

// ============================================================
// Motion — 4 niveaux
// ============================================================
export const motion = {
  // Repete (survol ligne, clic onglet, hover bouton) : presque imperceptible
  instant:    { duration: 120, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
  // Transitions UI normales (panneaux, accordeons, dropdowns)
  standard:   { duration: 200, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
  // Moments-cles avec overshoot calibre (ouverture modale, app launch)
  expressive: { duration: 420, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
  // Apparition resultat (easeOutExpo, decompression elegante)
  reveal:     { duration: 600, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
};

/**
 * Detecte prefers-reduced-motion: reduce.
 * SSR-safe : retourne false si window/matchMedia indisponibles.
 */
export function getReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Duree en ms pour un niveau de motion donne.
 * Si reduced-motion : retourne 0, sauf 'instant' qui garde 80ms
 * (besoin d'un feedback minimal sur les toggles).
 */
export function getMotionDuration(level, reducedMotionOverride) {
  const reduced = reducedMotionOverride ?? getReducedMotion();
  if (reduced) return level === 'instant' ? 80 : 0;
  return motion[level]?.duration ?? motion.standard.duration;
}

/**
 * Easing CSS pour un niveau donne.
 */
export function getMotionEasing(level) {
  return motion[level]?.easing ?? motion.standard.easing;
}

/**
 * Construit une chaine CSS de transition.
 * Exemple :
 *   style={{ transition: cssTransition('standard', ['background', 'border-color']) }}
 *   -> "background 200ms cubic-bezier(...), border-color 200ms cubic-bezier(...)"
 */
export function cssTransition(level, properties = ['all']) {
  const dur = getMotionDuration(level);
  const ease = getMotionEasing(level);
  return properties.map((p) => `${p} ${dur}ms ${ease}`).join(', ');
}

// ============================================================
// Bascule theme — injection CSS variables une seule fois
// ============================================================

const STYLE_ID = 'fg-os-theme-vars';

/**
 * Convertit un objet de tokens (cles camelCase) en lignes CSS vars (kebab-case).
 *   bgElevated -> --bg-elevated
 */
function tokensToCssLines(themeTokens) {
  return Object.entries(themeTokens)
    .map(([key, value]) => {
      const cssVar = '--' + key.replace(/([A-Z])/g, '-$1').toLowerCase();
      return `  ${cssVar}: ${value};`;
    })
    .join('\n');
}

/**
 * Genere le bloc CSS contenant les variables pour les deux themes.
 */
function generateThemeStyles() {
  return [
    `:root[data-theme="light"] {`,
    tokensToCssLines(tokens.light),
    `}`,
    `:root[data-theme="dark"] {`,
    tokensToCssLines(tokens.dark),
    `}`,
  ].join('\n');
}

/**
 * Injecte le bloc <style id="fg-os-theme-vars"> dans le <head>.
 * Idempotent : si deja injecte, ne fait rien.
 * SSR-safe : noop si document indisponible.
 */
function injectThemeStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = generateThemeStyles();
  document.head.appendChild(style);
}

/**
 * Applique un theme :
 *   1. Injecte le bloc CSS (une seule fois).
 *   2. Set data-theme="..." sur :root.
 *
 * Bascule instantanee : pas de transition globale (eviterait un repaint massif).
 * Les composants individuels peuvent ajouter leur propre transition via cssTransition().
 */
export function applyTheme(theme) {
  if (theme !== 'light' && theme !== 'dark') {
    console.warn(`[tokens] applyTheme: theme "${theme}" invalide, fallback light`);
    theme = 'light';
  }
  injectThemeStyles();
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
  }
}

/**
 * Retourne le theme actuellement applique au DOM, ou null si pas encore.
 */
export function getCurrentTheme() {
  if (typeof document === 'undefined') return null;
  return document.documentElement.getAttribute('data-theme');
}
